/**
 * App Controller for Poker Reborn
 *
 * Manages app lifecycle: initialization, connection, authentication, navigation.
 */

import { Application } from 'pixi.js';
import { appState, setScreen, setConnected, setPlayer } from '@/state/AppState';
import { networkService } from '@/services/NetworkService';
import { audioService } from '@/services/AudioService';
import { layoutService } from '@/services/LayoutService';
import { EventEmitter } from '@/services/EventEmitter';
import { log } from '@/utils/log';
import type { ScreenType } from '@/types/ui';
import type { PlayerProfile } from '@/types/game';

// =============================================================================
// TYPES
// =============================================================================

export interface AppControllerEvents {
  /** App initialized */
  initialized: undefined;

  /** Screen changed */
  screenChange: { screen: ScreenType };

  /** Loading progress */
  loadProgress: { progress: number; status: string };

  /** Error occurred */
  error: { message: string; fatal: boolean };
}

export interface InitOptions {
  /** Canvas element or selector */
  canvas?: HTMLCanvasElement | string;

  /** Server URL override */
  serverUrl?: string;

  /** Auto-login credentials */
  credentials?: { username: string; password: string };
}

// =============================================================================
// APP CONTROLLER
// =============================================================================

class AppController extends EventEmitter<AppControllerEvents> {
  private static instance: AppController | null = null;

  private app: Application | null = null;
  private initialized = false;

  private constructor() {
    super();
  }

  /** Get singleton instance */
  static getInstance(): AppController {
    if (!AppController.instance) {
      AppController.instance = new AppController();
    }
    return AppController.instance;
  }

  /** Get PixiJS application */
  getApp(): Application | null {
    return this.app;
  }

  /**
   * Initialize the application
   */
  async init(options: InitOptions = {}): Promise<void> {
    if (this.initialized) {
      log.app.warning`AppController already initialized`;
      return;
    }

    try {
      // Step 1: Create PixiJS application
      this.emit('loadProgress', { progress: 0, status: 'Creating renderer...' });
      await this.createApp(options.canvas);

      // Step 2: Initialize services
      this.emit('loadProgress', { progress: 20, status: 'Initializing services...' });
      await this.initServices();

      // Step 3: Connect to server (if credentials provided)
      this.emit('loadProgress', { progress: 60, status: 'Ready to connect...' });
      if (options.credentials) {
        this.emit('loadProgress', { progress: 70, status: 'Connecting...' });
        await this.connect(options.credentials.username, options.credentials.password);
      }

      // Step 4: Complete
      this.emit('loadProgress', { progress: 100, status: 'Ready!' });

      this.initialized = true;
      this.emit('initialized', undefined);

      // Navigate to lobby
      this.navigateTo('lobby');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      log.app.error`App initialization failed: ${error}`;
      this.emit('error', { message, fatal: true });
      throw error;
    }
  }

  /**
   * Navigate to a screen
   */
  navigateTo(screen: ScreenType): void {
    setScreen(screen);
    this.emit('screenChange', { screen });
  }

  /**
   * Connect and authenticate to server
   */
  async connect(username: string, password: string): Promise<void> {
    try {
      await networkService.connect(username, password);
      setConnected(true);

      // Create mock player profile for now
      const mockProfile: PlayerProfile = {
        id: networkService.getMyGlid(),
        name: username,
      };
      setPlayer(mockProfile);
      appState.isAuthenticated.set(true);
      appState.playerChips.set(10000); // Mock starting chips
    } catch (error) {
      setConnected(false);
      throw error;
    }
  }

  /**
   * Logout
   */
  logout(): void {
    networkService.disconnect();
    appState.isAuthenticated.set(false);
    setConnected(false);
    setPlayer(null);
    this.navigateTo('loading');
  }

  /**
   * Get current screen
   */
  getCurrentScreen(): ScreenType {
    return appState.currentScreen.get();
  }

  /**
   * Check if app is ready
   */
  isReady(): boolean {
    return appState.isReady.get();
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private async createApp(canvas?: HTMLCanvasElement | string): Promise<void> {
    // Resolve canvas element
    let canvasElement: HTMLCanvasElement | undefined;
    if (typeof canvas === 'string') {
      canvasElement = document.querySelector(canvas) as HTMLCanvasElement | undefined;
    } else if (canvas instanceof HTMLCanvasElement) {
      canvasElement = canvas;
    }

    // Create PixiJS application
    this.app = new Application();

    const initOptions: Parameters<Application['init']>[0] = {
      background: '#1a472a', // Poker table green
      antialias: true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    };

    if (canvasElement) {
      initOptions.canvas = canvasElement;
      initOptions.resizeTo = canvasElement.parentElement ?? window;
    } else {
      initOptions.resizeTo = window;
    }

    await this.app.init(initOptions);

    // If no canvas was provided, append to body
    if (!canvasElement && this.app.canvas) {
      document.body.appendChild(this.app.canvas);
    }
  }

  private async initServices(): Promise<void> {
    // Initialize layout service with app dimensions
    if (this.app) {
      layoutService.setScreenSize(
        this.app.screen.width,
        this.app.screen.height
      );
    }

    // Initialize audio (requires user interaction, but we prepare it)
    await audioService.init().catch(() => {
      // Audio init may fail before user interaction - that's OK
      log.app.info`Audio will initialize on first user interaction`;
    });

    // Setup network event handlers
    this.setupNetworkHandlers();
  }

  private setupNetworkHandlers(): void {
    networkService.on('disconnected', () => {
      setConnected(false);
    });

    networkService.on('error', (error) => {
      log.app.error`Network error: ${error}`;
      this.emit('error', { message: error.message, fatal: false });
    });
  }
}

/** Singleton app controller instance */
export const appController = AppController.getInstance();
